<template>
	<div class="card-page">
		<ja-header ref="header" title="JA Finance Park">
			<template v-slot:icon>
				<svgicon name="headerIcons/home"></svgicon>
			</template>
		</ja-header>

		<content>
			<div class="wrapper">
				<legend class="heading-1 question-box" aria-live="polite">
					{{ $t('chooseSimulation') }}
				</legend>
				<div v-if="simulations.length" class="simulations-list">
					<ja-card
						v-for="simulation in simulations[paginationData.currentPage - 1]"
						:key="simulation.id"
						:data-key="simulation.id"
						:selected="selectedSim.id == simulation.id"
						:aria-checked="(selectedSim.id == simulation.id).toString()"
						:class="isOptionDisabled(simulation.id) && 'card-selected'"
						@click.native="selectSimulation(simulation)"
					>
						{{ simulation.sessionName }}
						{{ isOptionDisabled(simulation.id) ? $t('currentSimulation') : '' }}
					</ja-card>

					<ja-pagination
						:data-per-page="paginationData.recordsPerPage"
						:data-count="paginationData.simulationsCount"
						@changePage="updatePagination($event)"
					>
					</ja-pagination>
				</div>

				<div class="btn-group btn-group--center">
					<ja-button
						name="proceed"
						:disabled="isProceedDisabled"
						class="large"
						@click.native="proceed"
					>
						{{ $t('proceed') }}
					</ja-button>
					<ja-button
						v-if="isCancelVisible"
						name="cancel"
						class="large outlined"
						@click.native="cancel"
					>
						{{ $t('cancel') }}
					</ja-button>
				</div>
			</div>
		</content>
	</div>
</template>

<script>
import Card from '@/components/ui/Card';
import Header from '@ui/headers/Header';
import Button from '@ui/Button';
import gql from 'graphql-tag';
import { convertUTCtoDateTime } from '@/helpers/utils';
import { UserData, ResetLifeStageAndCategories } from '@/mixins';
import Pagination from '@/components/AdminSection/elements/Pagination';
import {
	setChooseSimulation,
	getChooseSimulation,
	removeStorageReportSim,
	removeStorageRedirectTo
} from '@/helpers/storageFunctions';
import { sortBy, chunk } from 'lodash';

export default {
	components: {
		jaCard: Card,
		jaHeader: Header,
		jaButton: Button,
		jaPagination: Pagination
	},
	mixins: [UserData, ResetLifeStageAndCategories],
	data() {
		return {
			selectedSim: {},
			preselectedSim: {
				id: 0,
				sid: 0,
				sessionName: ''
			},
			simulations: [],
			paginationData: {
				currentPage: 1,
				recordsPerPage: 10,
				simulationsCount: 0
			},
			skipQuery: false
		};
	},
	computed: {
		isCancelVisible() {
			return !!getChooseSimulation('preselectedSim').id;
		},
		filters() {
			const criteria = [];
			if (this.userData) {
				criteria.push(`userId=${this.userData.userId}`);
				criteria.push(
					`simulation.StartDate < DateTime(${convertUTCtoDateTime()})`
				);
				criteria.push(
					`simulation.EndDate > DateTime(${convertUTCtoDateTime()})`
				);
			}

			return criteria.join('&&');
		},
		isProceedDisabled() {
			return !this.selectedSim.hasOwnProperty('id');
		}
	},
	mounted() {
		this.$store.commit('setLoading', true);
		this.preselectedSim = getChooseSimulation('preselectedSim');
		removeStorageReportSim();
	},
	apollo: {
		students: {
			query: gql`
				query simulations($where: String!) {
					students(
						where: $where
						order: "id desc"
						uniquefield: "simulationId"
					) {
						simulationId
						id
						simulation {
							sessionName
						}
					}
					simCount: getCountStudent(where: $where, uniquefield: "simulationId")
				}
			`,
			variables() {
				return {
					where: this.filters
				};
			},
			update(data) {
				this.paginationData.simulationsCount = data.simCount || 0;

				const simList = data.students.flatMap(element => {
					return {
						sessionName: element.simulation.sessionName,
						id: element.simulationId,
						sid: element.id
					};
				});
				// Sort the simulations by their name
				this.simulations = chunk(
					sortBy(simList, 'sessionName'),
					this.paginationData.recordsPerPage
				);

				this.$store.commit('setLoading', false);
			},
			skip() {
				return this.skipQuery || !this.userData;
			}
		}
	},
	methods: {
		selectSimulation(simObject) {
			this.selectedSim = simObject;
		},
		proceed() {
			setChooseSimulation(this.selectedSim);

			sessionStorage.removeItem('preselectedSim');

			// clear this as it is possible to create some errors
			sessionStorage.removeItem('redirectBackTo');

			this.$store.dispatch('reset').then(() => {
				// Constants are set only on "app create"
				// so we have to load them again when store is cleaned
				this.$store.dispatch('getConstants');
				this.$store.dispatch('clearBillboards');
				this.$store.dispatch('clearLocalSponsors');
				// because of reactivity in store we have to skip the query
				this.skipQuery = true;
				// when reset is ready show loading
				this.$store.commit('setLoading', true);
				// redirect when loading is visible
				this.$router.push({ path: '/' }).catch(e => e);
			});
		},
		cancel() {
			this.$store.commit('setLoading', true);
			// Update Local storage with already selected simulation info
			setChooseSimulation(this.preselectedSim);
			// Remove the key for next "change sim"
			sessionStorage.removeItem('preselectedSim');

			// removeStorageRecirectTo with param true will return the value before deletion
			let redirectBackTo = removeStorageRedirectTo(true) || 'home';

			// Refetch Lifestage data and Categories
			if (redirectBackTo === 'home' || redirectBackTo === 'category') {
				this.resetLifeStageAndCategories();
			}
			// Used Push instead of go(-1) because of the history stack.
			// Also this will redirect user (from BE) to where they have to be before
			// landing on this page.

			this.$router.push({ name: redirectBackTo }).catch(e => e);
		},
		isOptionDisabled(id) {
			return this.preselectedSim.id === id;
		},
		updatePagination(page) {
			this.paginationData.currentPage = page || 1;
		}
	},
	beforeRouteEnter(to, from, next) {
		// Swap the objects in case browser's back button is pressed
		// instead of "Change sim" button.
		if (window.popStateDetected) {
			// Get current simulation Object
			const sim = getChooseSimulation();
			// remove the key from storage
			sessionStorage.removeItem('simulationObject');
			// set the object as temporary object
			setChooseSimulation(sim, 'preselectedSim');
			// reset variable for further similar actions
			window.popStateDetected = false;
		}
		// Continue with the request
		next();
	}
};
</script>

<style lang="scss" scoped>
content {
	width: 100%;
	overflow-y: auto;
	flex-grow: 1;
	padding-top: 32px;

	.btn-group {
		margin-top: 52px;
	}

	.wrapper {
		width: 66%;
		max-width: 640px;
		margin: auto;
		margin-bottom: 48px;

		.question-box {
			text-align: center;
		}
	}
}

.question-box {
	margin-top: 0;
	margin-bottom: 32px;
	outline: none;
}
.card-selected {
	border: 3px solid #0094f0;
}
</style>
